import React, { useEffect, useState, useCallback } from "react";
import ThemeProvider from "../theme/TemaMuiProvider";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  InputAdornment,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import SearchIcon from "@mui/icons-material/Search";
import { jwtDecode } from "jwt-decode";
import muiTheme from "../theme/muiTheme";
import { copiarTexto } from "../utils/clipboard";

const ACAO_COR = {
  Criou: "success",
  Editou: "info",
  Excluiu: "error",
  Inutilizou: "error",
  Reativou: "success",
  "Enviou para Aprovação": "info",
  Aprovou: "success",
  Reprovou: "error",
  Questionou: "warning",
  Respondeu: "info",
  Revisou: "warning",
  Reabriu: "warning",
};

function formatarDataISO(d) {
  const ano = d.getFullYear();
  const mes = String(d.getMonth() + 1).padStart(2, "0");
  const dia = String(d.getDate()).padStart(2, "0");
  return `${ano}-${mes}-${dia}`;
}

function hojeISO() {
  return formatarDataISO(new Date());
}

function diasAtrasISO(dias) {
  const d = new Date();
  d.setDate(d.getDate() - dias);
  return formatarDataISO(d);
}

function formatarDataHora(iso) {
  if (!iso) return "—";
  const formatado = new Date(iso).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  return `${formatado}h`;
}

function LinhaDetalhe({ alt }) {
  return (
    <TableRow sx={{ backgroundColor: "action.hover" }}>
      <TableCell sx={{ pl: 4, fontSize: "0.8rem", color: "text.secondary" }}>{alt.campoLabel}</TableCell>
      <TableCell
        sx={{
          fontSize: "0.8rem",
          fontStyle: alt.valorAntes ? "normal" : "italic",
          color: alt.valorAntes ? "text.primary" : "text.disabled",
        }}
      >
        {alt.valorAntes ?? "—"}
      </TableCell>
      <TableCell
        sx={{
          fontSize: "0.8rem",
          fontStyle: alt.valorDepois ? "normal" : "italic",
          color: alt.valorDepois ? "text.primary" : "text.disabled",
        }}
      >
        {alt.valorDepois ?? "—"}
      </TableCell>
    </TableRow>
  );
}

// Agrupa alterações de sublistas por (modelo, registroId)
function gruparFilhos(alteracoes, modeloPai) {
  const grupos = [];
  const mapa = new Map();
  for (const alt of alteracoes) {
    if (alt.modelo === modeloPai) continue;
    const chave = `${alt.modelo}:${alt.registroId}`;
    if (!mapa.has(chave)) {
      const grupo = {
        modelo: alt.modelo,
        registroId: alt.registroId,
        alts: [],
      };
      mapa.set(chave, grupo);
      grupos.push(grupo);
    }
    mapa.get(chave).alts.push(alt);
  }
  return grupos;
}

function LinhaLog({ row, labelModulo }) {
  const [expandido, setExpandido] = useState(false);

  const alteracoesPai = row.alteracoes.filter((a) => a.modelo === row.modelo);
  const grupos = gruparFilhos(row.alteracoes, row.modelo);

  return (
    <>
      <TableRow hover>
        <TableCell sx={{ width: 36, p: 0.5 }}>
          <Tooltip title={expandido ? "Recolher" : "Ver alterações"}>
            <span>
              <IconButton size="small" onClick={() => setExpandido((v) => !v)} disabled={row.alteracoes.length === 0}>
                {expandido ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        </TableCell>
        <TableCell sx={{ fontSize: "0.82rem" }}>{labelModulo(row.modelo)}</TableCell>
        <TableCell sx={{ whiteSpace: "nowrap", fontSize: "0.82rem" }}>{formatarDataHora(row.dataHora)}</TableCell>
        <TableCell sx={{ fontSize: "0.82rem" }}>{row.usuarioNome}</TableCell>
        <TableCell>
          <Chip label={row.acao} color={ACAO_COR[row.acao] ?? "default"} size="small" />
        </TableCell>
        <TableCell sx={{ fontSize: "0.82rem", textAlign: "center" }}>{row.registroId}</TableCell>
        <TableCell sx={{ fontSize: "0.82rem" }}>
          {row.identificador ?? (
            <Typography variant="caption" color="text.disabled">
              —
            </Typography>
          )}
        </TableCell>
        <TableCell sx={{ fontSize: "0.82rem", textAlign: "center" }}>
          {row.alteracoes.length > 0 ? (
            <Chip label={`${row.alteracoes.length} campo${row.alteracoes.length !== 1 ? "s" : ""}`} size="small" variant="outlined" />
          ) : (
            <Typography variant="caption" color="text.disabled">
              nenhuma
            </Typography>
          )}
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell colSpan={8} sx={{ p: 0, border: 0 }}>
          <Collapse in={expandido} timeout="auto" unmountOnExit>
            <Box sx={{ m: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: "text.secondary",
                        fontSize: "0.75rem",
                      }}
                    >
                      Campo
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: "text.secondary",
                        fontSize: "0.75rem",
                      }}
                    >
                      Antes
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: "text.secondary",
                        fontSize: "0.75rem",
                      }}
                    >
                      Depois
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {alteracoesPai.map((alt) => (
                    <LinhaDetalhe key={alt.id} alt={alt} />
                  ))}
                  {grupos.map((grupo, idx) => (
                    <React.Fragment key={`${grupo.modelo}:${grupo.registroId}`}>
                      <TableRow>
                        <TableCell
                          colSpan={3}
                          sx={{
                            fontWeight: 700,
                            fontSize: "0.75rem",
                            pt: idx === 0 ? 1.5 : 2,
                            pb: 0.5,
                            borderBottom: "none",
                            borderTop: idx > 0 ? "1px solid" : "none",
                            borderTopColor: "divider",
                            color: "primary.main",
                          }}
                        >
                          {grupo.modelo} #{grupo.registroId}
                        </TableCell>
                      </TableRow>
                      {grupo.alts.map((alt) => (
                        <LinhaDetalhe key={alt.id} alt={alt} />
                      ))}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

function Auditoria() {
  const token = localStorage.getItem("token");
  let tipoUsuario = "";
  if (token) {
    const decoded = jwtDecode(token);
    tipoUsuario = decoded.tipo?.toString().trim().toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
  }
  const temPermissao = ["gestor", "administrador"].includes(tipoUsuario);

  const [modelos, setModelos] = useState([]);
  const [modelo, setModelo] = useState("");

  const [dados, setDados] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState("");

  const [inputFiltro, setInputFiltro] = useState("");
  const [filtro, setFiltro] = useState("");
  const [de, setDe] = useState(() => diasAtrasISO(30));
  const [ate, setAte] = useState(() => hojeISO());
  const [pagina, setPagina] = useState(0);
  const [porPagina, setPorPagina] = useState(25);

  // Carrega lista de modelos disponíveis
  useEffect(() => {
    if (!temPermissao) return;
    fetch("/api/auditoria/modelos", {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((lista) => {
        setModelos(Array.isArray(lista) ? lista : []);
      })
      .catch((err) => console.error("Erro ao carregar modelos:", err));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const labelModulo = useCallback((value) => modelos.find((m) => m.value === value)?.label ?? value, [modelos]);

  const buscar = useCallback(() => {
    if (!temPermissao) return;
    setLoading(true);
    setErro("");
    const params = new URLSearchParams({ pagina, porPagina });
    if (modelo) params.set("modelo", modelo);
    if (filtro.trim()) params.set("filtro", filtro.trim());
    if (de) params.set("de", de);
    if (ate) params.set("ate", ate);

    fetch(`/api/auditoria/logs?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => {
        if (!r.ok)
          return r.json().then((d) => {
            throw new Error(d.erro || `HTTP ${r.status}`);
          });
        return r.json();
      })
      .then((d) => {
        setDados(d.data ?? []);
        setTotal(d.total ?? 0);
      })
      .catch((err) => setErro(err.message))
      .finally(() => setLoading(false));
  }, [temPermissao, modelo, filtro, de, ate, pagina, porPagina, token]);

  useEffect(() => {
    buscar();
  }, [buscar]);

  const handlePesquisar = () => {
    setFiltro(inputFiltro);
    setPagina(0);
  };

  const handleLimparFiltro = () => {
    setInputFiltro("");
    setFiltro("");
    setPagina(0);
  };

  // =======================
  // COPIAR
  // =======================
  const handleCopiar = async () => {
    const texto = dados
      .map((row) => {
        const modulo = labelModulo(row.modelo);
        const dataHora = formatarDataHora(row.dataHora);
        const usuario = row.usuarioNome || "";
        const acao = row.acao || "";
        const registroId = row.registroId ?? "";
        const identificador = row.identificador ?? "";

        const alteracoes = row.alteracoes?.length > 0 ? `${row.alteracoes.length} campo${row.alteracoes.length !== 1 ? "s" : ""}` : "nenhuma";

        return [modulo, dataHora, usuario, acao, registroId, identificador, alteracoes].join(" - ");
      })
      .join(", ");

    if (!texto.trim()) {
      alert("Nada para copiar (lista vazia).");
      return;
    }

    const ok = await copiarTexto(texto);

    alert(ok ? "Copiado!" : "Não foi possível copiar.");
  };

  // =======================
  // CSV
  // =======================
  const handleCSV = () => {
    const BOM = "\uFEFF";

    const header = ["Módulo", "Data / Hora", "Usuário", "Ação", "Reg. ID", "Código/Nome/Descrição", "Alterações"];

    const csvEscape = (value) => {
      const texto = String(value ?? "");

      if (/[;"\n\r]/.test(texto)) {
        return `"${texto.replace(/"/g, '""')}"`;
      }

      return texto;
    };

    const linhas = dados.map((row) => {
      const alteracoes = row.alteracoes?.length > 0 ? `${row.alteracoes.length} campo${row.alteracoes.length !== 1 ? "s" : ""}` : "nenhuma";

      return [
        labelModulo(row.modelo),
        formatarDataHora(row.dataHora),
        row.usuarioNome || "",
        row.acao || "",
        row.registroId ?? "",
        row.identificador ?? "",
        alteracoes,
      ].map(csvEscape);
    });

    const csv = [header.map(csvEscape), ...linhas].map((linha) => linha.join(";")).join("\n");

    const blob = new Blob([BOM + csv], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;

    link.setAttribute("download", "auditoria.csv");

    document.body.appendChild(link);

    link.click();

    link.remove();

    URL.revokeObjectURL(url);
  };

  if (!temPermissao) {
    return (
      <ThemeProvider theme={muiTheme}>
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="h5" gutterBottom>
            Auditoria
          </Typography>
          <Typography color="text.secondary">🔒 Acesso restrito a gestores e administradores.</Typography>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={muiTheme}>
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Auditoria
        </Typography>

        {erro && (
          <Box sx={{ mb: 2, p: 1.5, bgcolor: "error.dark", borderRadius: 1 }}>
            <Typography color="error.contrastText" variant="body2">
              Erro: {erro}
            </Typography>
          </Box>
        )}

        {/* Filtro + Copiar + CSV */}
        <Box sx={{ display: "flex", gap: 1, mb: 1, alignItems: "center" }}>
          <TextField
            label="Filtrar Auditoria"
            size="small"
            value={inputFiltro}
            onChange={(e) => setInputFiltro(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handlePesquisar();
            }}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon sx={{ color: "var(--ig-t-9ca3af)", fontSize: 20 }} />
                  </InputAdornment>
                ),
              },
            }}
            sx={{ flex: 1 }}
          />

          <Button
            size="small"
            variant="outlined"
            type="button"
            onClick={handleLimparFiltro}
            sx={{
              flexShrink: 0,
              width: "auto",
              height: 40,
              color: "#2196f3",
              borderColor: "#2196f3",
              fontWeight: 600,
              textTransform: "none",
              whiteSpace: "nowrap",
              "&:hover": {
                borderColor: "#42a5f5",
                backgroundColor: "rgba(33, 150, 243, 0.08)",
              },
            }}
          >
            Limpar Filtro
          </Button>

          <Button
            size="small"
            variant="contained"
            type="button"
            onClick={handleCopiar}
            sx={{
              height: 40,
              backgroundColor: "#2196f3",
              fontWeight: 600,
              textTransform: "none",
              "&:hover": {
                backgroundColor: "#1976d2",
              },
            }}
          >
            <i
              className="fas fa-copy"
              style={{
                marginRight: 6,
              }}
            />
            Copiar
          </Button>

          <Button
            size="small"
            variant="contained"
            type="button"
            onClick={handleCSV}
            sx={{
              height: 40,
              backgroundColor: "#2196f3",
              fontWeight: 600,
              textTransform: "none",
              "&:hover": {
                backgroundColor: "#1976d2",
              },
            }}
          >
            CSV
          </Button>
        </Box>

        {/* Módulo + Datas */}
        <Box sx={{ display: "flex", gap: 1.5, mb: 2, alignItems: "flex-end" }}>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Módulo</InputLabel>
            <Select
              value={modelo}
              label="Módulo"
              onChange={(e) => {
                setModelo(e.target.value);
                setPagina(0);
              }}
            >
              <MenuItem value="">Todos os Módulos</MenuItem>
              {modelos.map((m) => (
                <MenuItem key={m.value} value={m.value}>
                  {m.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="De"
            type="date"
            size="small"
            slotProps={{ inputLabel: { shrink: true } }}
            value={de}
            onChange={(e) => {
              setDe(e.target.value);
              setPagina(0);
            }}
            sx={{ minWidth: 190 }}
          />

          <TextField
            label="Até"
            type="date"
            size="small"
            slotProps={{ inputLabel: { shrink: true } }}
            value={ate}
            onChange={(e) => {
              setAte(e.target.value);
              setPagina(0);
            }}
            sx={{ minWidth: 190 }}
          />
        </Box>

        {/* Tabela */}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 36 }} />
                  <TableCell sx={{ fontWeight: 600 }}>Módulo</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Data / Hora</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Usuário</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Ação</TableCell>
                  <TableCell sx={{ fontWeight: 600, textAlign: "center" }}>Reg. ID</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Código/Nome/Descrição</TableCell>
                  <TableCell sx={{ fontWeight: 600, textAlign: "center" }}>Alterações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dados.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      sx={{
                        textAlign: "center",
                        py: 4,
                        color: "text.secondary",
                      }}
                    >
                      Nenhum registro encontrado.
                    </TableCell>
                  </TableRow>
                ) : (
                  dados.map((row) => <LinhaLog key={row.id} row={row} labelModulo={labelModulo} />)
                )}
              </TableBody>
            </Table>

            <TablePagination
              component="div"
              count={total}
              page={pagina}
              onPageChange={(_, p) => setPagina(p)}
              rowsPerPage={porPagina}
              onRowsPerPageChange={(e) => {
                setPorPagina(parseInt(e.target.value, 10));
                setPagina(0);
              }}
              rowsPerPageOptions={[10, 25, 50]}
              labelRowsPerPage="Por página:"
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
            />
          </TableContainer>
        )}
      </Box>
    </ThemeProvider>
  );
}

export default Auditoria;
